import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Folder, Camera, Check, Gift, Clock, Star, Trophy, Cloud, RefreshCw } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '../../lib/api'

interface FridayFolderData {
  folder_date: string
  is_friday_weekend: boolean
  folder: {
    id: number
    items_scanned: number
    items_reviewed: number
    is_complete: boolean
    total_xp_earned: number
    started_at: string | null
    completed_at: string | null
  } | null
  scans: Array<{
    id: number
    scan_type: string
    subject: string | null
    title: string | null
    grade_detected: number | null
    xp_awarded: number
    created_at: string
  }>
  bonuses: {
    first_item: { earned: boolean; xp: number }
    complete_folder: { earned: boolean; xp: number; min_items: number }
    early_bird: { earned: boolean; available: boolean; xp: number; deadline: string }
    review_all: { earned: boolean; xp: number }
  }
  config: { scan_xp: number }
}

const SCAN_TYPES = [
  { value: 'test', label: 'Test' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'homework', label: 'Homework' },
  { value: 'worksheet', label: 'Worksheet' },
]

const SUBJECTS = [
  'Math', 'Reading', 'Writing', 'Science',
  'Social Studies', 'Spanish', 'Other'
]

export function FridayFolder() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [showScanModal, setShowScanModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [scanType, setScanType] = useState('test')
  const [subject, setSubject] = useState('')
  const [title, setTitle] = useState('')
  const [grade, setGrade] = useState('')
  const [lastResult, setLastResult] = useState<any>(null)

  const { data, isLoading } = useQuery<FridayFolderData>({
    queryKey: ['friday-folder'],
    queryFn: async () => {
      const response = await api.get('/api/student/friday-folder/current')
      return response.data
    },
  })

  // Auto-check cloud storage on component mount (once per session)
  const { data: cloudData, isFetching: isCheckingCloud } = useQuery({
    queryKey: ['friday-folder-cloud-check'],
    queryFn: async () => {
      const response = await api.post('/api/student/friday-folder/process-cloud')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // Only check once every 5 minutes
    refetchOnWindowFocus: false,
    retry: false, // Don't retry on failure
  })

  // Show notification if cloud check found new documents
  const [cloudNotificationShown, setCloudNotificationShown] = useState(false)

  // Update cloudResult when auto-check completes
  useEffect(() => {
    if (cloudData && cloudData.total_documents > 0 && !cloudNotificationShown) {
      setCloudResult({
        total_documents: cloudData.total_documents,
        errors: cloudData.errors || 0,
      })
      setCloudNotificationShown(true)
      queryClient.invalidateQueries({ queryKey: ['friday-folder'] })
    }
  }, [cloudData, cloudNotificationShown, queryClient])

  const scanMutation = useMutation({
    mutationFn: async (scanData: any) => {
      const response = await api.post('/api/student/friday-folder/scan', scanData)
      return response.data
    },
    onSuccess: (result) => {
      setLastResult(result)
      queryClient.invalidateQueries({ queryKey: ['friday-folder'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      setTimeout(() => {
        setSelectedImage(null)
        setScanType('test')
        setSubject('')
        setTitle('')
        setGrade('')
        setShowScanModal(false)
        setLastResult(null)
      }, 3000)
    },
  })

  const completeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/student/friday-folder/complete')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friday-folder'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
    },
  })

  // Process cloud scans (Drive/Dropbox)
  const [cloudResult, setCloudResult] = useState<{
    total_documents: number
    errors: number
  } | null>(null)

  const processCloudMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/student/friday-folder/process-cloud')
      return response.data
    },
    onSuccess: (result) => {
      setCloudResult({
        total_documents: result.total_documents,
        errors: result.errors,
      })
      queryClient.invalidateQueries({ queryKey: ['friday-folder'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['scan-history'] })
      // Clear result after delay
      setTimeout(() => setCloudResult(null), 5000)
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string)
        setShowScanModal(true)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmitScan = () => {
    if (!selectedImage) return
    const base64Data = selectedImage.split(',')[1]
    scanMutation.mutate({
      scan_type: scanType,
      subject: subject || undefined,
      title: title || undefined,
      grade: grade ? parseFloat(grade) : undefined,
      image_data: base64Data,
    })
  }

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-gray-700 rounded"></div>
      </div>
    )
  }

  if (!data) return null

  // Only show prominently on Friday-Sunday
  const isWeekend = data.is_friday_weekend
  const folder = data.folder
  const bonuses = data.bonuses

  return (
    <>
      <div className={`card ${isWeekend ? 'border-quest-accent/50 bg-quest-accent/5' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isWeekend ? 'bg-quest-accent/20' : 'bg-gray-700'
            }`}>
              <Folder className={`w-5 h-5 ${isWeekend ? 'text-quest-accent' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-bold flex items-center gap-2">
                Friday Folder
                {isWeekend && !folder?.is_complete && (
                  <span className="px-2 py-0.5 bg-quest-accent/20 text-quest-accent text-xs rounded-full">
                    Ready!
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-400">
                {new Date(data.folder_date).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric'
                })}
              </p>
            </div>
          </div>

          {!folder?.is_complete && (
            <div className="flex items-center gap-2">
              {/* Sync status indicator */}
              {(isCheckingCloud || processCloudMutation.isPending) ? (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Syncing...
                </span>
              ) : (
                <button
                  onClick={() => processCloudMutation.mutate()}
                  className="p-1.5 rounded hover:bg-surface-hover transition-colors"
                  title="Sync from cloud storage"
                >
                  <Cloud className="w-4 h-4 text-gray-500" />
                </button>
              )}
              {/* Manual scan button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-primary btn-sm flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Scan
              </button>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Cloud processing result notification */}
        {cloudResult && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${
            cloudResult.total_documents > 0
              ? 'bg-green-900/30 border border-green-700/30'
              : 'bg-blue-900/30 border border-blue-700/30'
          }`}>
            {cloudResult.total_documents > 0 ? (
              <>
                <Check className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-green-400">
                    Found {cloudResult.total_documents} new document{cloudResult.total_documents > 1 ? 's' : ''}!
                  </p>
                  <p className="text-sm text-green-300/70">
                    Review them below to earn XP
                  </p>
                </div>
              </>
            ) : (
              <>
                <Cloud className="w-5 h-5 text-blue-400" />
                <p className="text-blue-300">No new scans found in cloud storage</p>
              </>
            )}
            {cloudResult.errors > 0 && (
              <span className="text-xs text-orange-400 ml-auto">
                {cloudResult.errors} error{cloudResult.errors > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Progress */}
        {folder && folder.items_scanned > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Items scanned</span>
              <span className="font-medium">{folder.items_scanned}</span>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: Math.max(5, folder.items_scanned) }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 flex-1 rounded ${
                    i < folder.items_scanned ? 'bg-quest-primary' : 'bg-gray-700'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Scanned items */}
        {data.scans.length > 0 && (
          <div className="space-y-2 mb-4">
            {data.scans.map((scan) => (
              <div
                key={scan.id}
                className="flex items-center justify-between bg-surface-card/50 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">
                    {scan.title || scan.subject || scan.scan_type}
                  </span>
                  {scan.grade_detected && (
                    <span className="text-xs bg-quest-primary/20 text-quest-primary px-2 py-0.5 rounded">
                      {scan.grade_detected}%
                    </span>
                  )}
                </div>
                <span className="text-sm text-quest-accent">+{scan.xp_awarded} XP</span>
              </div>
            ))}
          </div>
        )}

        {/* Bonuses */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <BonusBadge
            icon={<Star className="w-4 h-4" />}
            label="First Item"
            earned={bonuses.first_item.earned}
            xp={bonuses.first_item.xp}
          />
          <BonusBadge
            icon={<Clock className="w-4 h-4" />}
            label="Early Bird"
            earned={bonuses.early_bird.earned}
            available={bonuses.early_bird.available}
            xp={bonuses.early_bird.xp}
          />
          <BonusBadge
            icon={<Trophy className="w-4 h-4" />}
            label={`Complete (${bonuses.complete_folder.min_items}+)`}
            earned={bonuses.complete_folder.earned}
            xp={bonuses.complete_folder.xp}
          />
          <BonusBadge
            icon={<Check className="w-4 h-4" />}
            label="Review All"
            earned={bonuses.review_all.earned}
            xp={bonuses.review_all.xp}
          />
        </div>

        {/* Complete button */}
        {folder && folder.items_scanned >= bonuses.complete_folder.min_items && !folder.is_complete && (
          <button
            onClick={() => completeMutation.mutate()}
            disabled={completeMutation.isPending}
            className="w-full btn btn-primary flex items-center justify-center gap-2"
          >
            <Gift className="w-4 h-4" />
            {completeMutation.isPending ? 'Completing...' : 'Mark Folder Complete'}
          </button>
        )}

        {/* Completed state */}
        {folder?.is_complete && (
          <div className="text-center py-4 bg-green-900/20 rounded-lg border border-green-700/30">
            <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="font-medium text-green-400">Folder Complete!</p>
            <p className="text-sm text-gray-400">Total XP: {folder.total_xp_earned}</p>
          </div>
        )}

        {/* Empty state */}
        {!folder && (
          <div className="text-center py-4 text-gray-500">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {isWeekend
                ? "Scan your Friday Folder items to earn bonus XP!"
                : "Friday Folder opens on Fridays"}
            </p>
          </div>
        )}
      </div>

      {/* Scan Modal */}
      <AnimatePresence>
        {showScanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-surface-card rounded-xl p-6 w-full max-w-md"
            >
              {lastResult ? (
                <div className="text-center py-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <Check className="w-8 h-8 text-green-500" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-2">Scanned!</h3>
                  <p className="text-2xl font-bold text-quest-accent mb-2">
                    +{lastResult.xp_awarded} XP
                  </p>
                  {lastResult.bonuses_earned?.length > 0 && (
                    <div className="text-sm text-quest-accent">
                      Bonuses: {lastResult.bonuses_earned.join(', ')}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold mb-4">Friday Folder Scan</h3>

                  {selectedImage && (
                    <div className="mb-4 rounded-lg overflow-hidden">
                      <img
                        src={selectedImage}
                        alt="Scan preview"
                        className="w-full h-32 object-cover"
                      />
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Type</label>
                        <select
                          value={scanType}
                          onChange={(e) => setScanType(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        >
                          {SCAN_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Subject</label>
                        <select
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        >
                          <option value="">Select...</option>
                          {SUBJECTS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Title</label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., Chapter 5 Test"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Grade %</label>
                      <input
                        type="number"
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                        placeholder="e.g., 92"
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => {
                          setShowScanModal(false)
                          setSelectedImage(null)
                        }}
                        className="flex-1 btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitScan}
                        disabled={scanMutation.isPending}
                        className="flex-1 btn btn-primary"
                      >
                        {scanMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function BonusBadge({
  icon,
  label,
  earned,
  available = true,
  xp
}: {
  icon: React.ReactNode
  label: string
  earned: boolean
  available?: boolean
  xp: number
}) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
      earned
        ? 'bg-green-900/30 border border-green-700/30'
        : available
          ? 'bg-surface-card/50 border border-gray-700/30'
          : 'bg-surface-card/30 border border-gray-700/20 opacity-50'
    }`}>
      <div className={earned ? 'text-green-500' : 'text-gray-500'}>
        {earned ? <Check className="w-4 h-4" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs truncate ${earned ? 'text-green-400' : 'text-gray-400'}`}>
          {label}
        </p>
      </div>
      <span className={`text-xs font-medium ${earned ? 'text-green-400' : 'text-gray-500'}`}>
        +{xp}
      </span>
    </div>
  )
}
